<template>
  <el-dialog
    title="配置阶段工期"
    :visible.sync="dialogVisible"
    append-to-body
    :close-on-click-modal="false"
    width="720px"
    @close="handleClose"
  >
    <!-- 套餐信息头 -->
    <div class="schedule-combo-info">
      <i class="el-icon-collection-tag"></i>
      <span class="schedule-combo-info__name">{{ comboName }}</span>
      <span class="schedule-combo-info__tip">拖拽行可调整阶段顺序</span>
    </div>

    <!-- 加载中 -->
    <div v-if="loading" class="schedule-loading">
      <i class="el-icon-loading"></i>
      <span>正在加载工期配置…</span>
    </div>

    <!-- 阶段工期列表 -->
    <div v-else-if="stageList.length" class="schedule-panel">
      <div class="schedule-panel__header">
        <span class="schedule-col schedule-col--drag"></span>
        <span class="schedule-col schedule-col--sort">顺序</span>
        <span class="schedule-col schedule-col--name">阶段名称</span>
        <span class="schedule-col schedule-col--days">耗时天数（≥0.5，0.5的倍数）</span>
        <span class="schedule-col schedule-col--days">间隔天数（≥0，0.5的倍数）</span>
      </div>

      <draggable
        v-model="stageList"
        handle=".schedule-row__drag"
        :animation="200"
        @end="handleDragEnd"
      >
        <div
          v-for="(stage, index) in stageList"
          :key="stage.goodsId"
          class="schedule-row"
        >
          <span class="schedule-col schedule-col--drag">
            <i class="el-icon-rank schedule-row__drag"></i>
          </span>
          <span class="schedule-col schedule-col--sort">
            <span class="schedule-row__sort-badge">{{ index + 1 }}</span>
          </span>
          <span class="schedule-col schedule-col--name" :title="stage.stageName">
            {{ stage.stageName || stage.goodsId }}
          </span>
          <span class="schedule-col schedule-col--days">
            <el-input-number
              v-model="stage.requiredDays"
              :min="0.5"
              :step="0.5"
              size="small"
              controls-position="right"
              style="width: 130px"
            ></el-input-number>
          </span>
          <span class="schedule-col schedule-col--days">
            <el-input-number
              v-model="stage.intervalDays"
              :min="0"
              :step="0.5"
              size="small"
              controls-position="right"
              :disabled="index === stageList.length - 1"
              style="width: 130px"
            ></el-input-number>
            <el-tooltip
              v-if="index === stageList.length - 1"
              content="末阶段间隔固定为 0"
              placement="top"
            >
              <i class="el-icon-info schedule-row__last-tip"></i>
            </el-tooltip>
          </span>
        </div>
      </draggable>

      <!-- 底部汇总：总工期 = Σ耗时 + Σ间隔 -->
      <div class="schedule-panel__footer">
        <span>共 <b>{{ stageList.length }}</b> 个阶段</span>
        <span>
          预计总工期：
          <span class="schedule-panel__total">{{ totalDays }}</span> 天
          <span class="schedule-panel__formula">（Σ耗时 {{ totalRequiredDays }} + Σ间隔 {{ totalIntervalDays }}）</span>
        </span>
      </div>
    </div>

    <!-- 空状态 -->
    <div v-else class="schedule-empty">
      <i class="el-icon-folder-opened"></i>
      <p>该套餐暂无阶段数据，请先在套餐中配置组合阶段</p>
    </div>

    <span slot="footer" class="dialog-footer">
      <el-button @click="dialogVisible = false">取 消</el-button>
      <el-button
        type="primary"
        :loading="saveLoading"
        :disabled="loading || !stageList.length"
        @click="handleSave"
      >保 存</el-button>
    </span>
  </el-dialog>
</template>

<script>
import draggable from "vuedraggable";
import {
  getComboDetail,
  getStageSchedule,
  saveStageSchedule,
  findStageListByProjectId
} from "@/api/work/waterProof/combo";

export default {
  name: "comboScheduleDialog",

  components: { draggable },

  data() {
    return {
      dialogVisible: false,
      loading: false,
      saveLoading: false,

      // 当前套餐
      comboId: "",
      comboName: "",
      // 套餐的子产品 ID 集合（归属校验依据，统一为字符串）
      comboGoodsIds: [],

      // 工期配置行：{ goodsId, stageName, requiredDays, intervalDays, sort }
      stageList: []
    };
  },

  computed: {
    /** Σ耗时 */
    totalRequiredDays() {
      return this.stageList.reduce((sum, s) => sum + Number(s.requiredDays || 0), 0);
    },

    /** Σ间隔 */
    totalIntervalDays() {
      return this.stageList.reduce((sum, s) => sum + Number(s.intervalDays || 0), 0);
    },

    /** 总工期 = Σ耗时 + Σ间隔（与后端回写 required_days 逻辑一致） */
    totalDays() {
      return this.totalRequiredDays + this.totalIntervalDays;
    }
  },

  methods: {
    /**
     * 打开弹窗并加载数据
     * @param {Object} row - 套餐列表行（需含 id / name）
     */
    open(row) {
      this.comboId = row.id;
      this.comboName = row.name || "";
      this.comboGoodsIds = [];
      this.stageList = [];
      this.dialogVisible = true;
      this.loading = true;

      // 套餐详情（取子产品集合 + parentId）与已有工期明细并行加载
      Promise.all([
        getComboDetail(this.comboId),
        getStageSchedule(this.comboId)
      ])
        .then(([detailRes, scheduleRes]) => {
          const detail = this.parseData(detailRes);
          if (!detail) {
            this.$message.error("获取套餐详情失败");
            this.dialogVisible = false;
            return;
          }

          // 套餐子产品 ID 集合（优先 childIds，兜底拆 concatId）
          const childIds = Array.isArray(detail.childIds)
            ? detail.childIds.map((id) => String(id).trim())
            : (detail.concatId
              ? detail.concatId.split(",").map((id) => String(id).trim())
              : []);
          this.comboGoodsIds = childIds;

          const schedules = this.parseData(scheduleRes);
          const scheduleList = Array.isArray(schedules) ? schedules : [];

          // 阶段名称映射：优先用已有明细的 stageName，缺失再查项目阶段补齐
          if (detail.parentId) {
            return findStageListByProjectId(detail.parentId)
              .then((stageRes) => {
                const nameMap = this.buildStageNameMap(stageRes);
                this.buildStageList(childIds, scheduleList, nameMap);
              })
              .catch(() => {
                // 阶段名称加载失败不阻断，仅名称回退为已有明细/goodsId
                this.buildStageList(childIds, scheduleList, {});
              });
          }
          this.buildStageList(childIds, scheduleList, {});
        })
        .catch((err) => {
          console.error("加载工期配置失败:", err);
          this.$message.error("加载工期配置失败");
          this.dialogVisible = false;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    /** 解析响应 data（兼容 JSON 字符串 / 对象） */
    parseData(res) {
      if (!res) return null;
      if (res.code !== undefined && res.code !== "200" && res.code !== 200) {
        return null;
      }
      const data = res.data !== undefined ? res.data : res;
      if (typeof data === "string") {
        try {
          return JSON.parse(data);
        } catch (e) {
          return null;
        }
      }
      return data;
    },

    /** 从项目阶段接口响应中构建 goodsId → stageName 映射 */
    buildStageNameMap(res) {
      const nameMap = {};
      const parsed = this.parseData(res);
      if (!parsed) return nameMap;
      const projectData =
        parsed.rows && parsed.rows.length > 0 ? parsed.rows[0] : parsed;
      if (projectData && Array.isArray(projectData.list)) {
        projectData.list.forEach((stage) => {
          nameMap[String(stage.id || "")] = stage.name || "";
        });
      }
      return nameMap;
    },

    /**
     * 构建工期配置行：以套餐子产品集合为准（全量覆盖需完整列表）
     * 已配置的沿用明细值并按其 sort 排序，未配置的默认 耗时1天/间隔0天 追加在后
     */
    buildStageList(childIds, scheduleList, nameMap) {
      const scheduleMap = {};
      scheduleList.forEach((item) => {
        scheduleMap[String(item.goodsId)] = item;
      });

      const configured = [];
      const unconfigured = [];
      childIds.forEach((goodsId) => {
        const existed = scheduleMap[goodsId];
        if (existed) {
          configured.push({
            goodsId,
            stageName: existed.stageName || nameMap[goodsId] || "",
            requiredDays: Math.max(0.5, Number(existed.requiredDays) || 1),
            intervalDays: Math.max(0, Number(existed.intervalDays) || 0),
            sort: Number(existed.sort) || 0
          });
        } else {
          unconfigured.push({
            goodsId,
            stageName: nameMap[goodsId] || "",
            requiredDays: 1,
            intervalDays: 0,
            sort: 0
          });
        }
      });

      configured.sort((a, b) => a.sort - b.sort);
      this.stageList = configured.concat(unconfigured);
      this.normalizeLastInterval();
    },

    /** 拖拽结束后重置末阶段间隔 */
    handleDragEnd() {
      this.normalizeLastInterval();
    },

    /** 末阶段间隔固定为 0 */
    normalizeLastInterval() {
      if (this.stageList.length) {
        this.stageList[this.stageList.length - 1].intervalDays = 0;
      }
    },

    /** 是否为 0.5 的倍数（支持半天粒度） */
    isHalfDayMultiple(val) {
      return Number.isInteger(Number(val) * 2);
    },

    /**
     * 保存前完整业务校验
     * @returns {String|null} 错误信息，null 表示校验通过
     */
    validateStages() {
      if (!this.comboId) return "套餐ID不能为空";
      if (!this.stageList.length) return "阶段工期明细不能为空";

      const goodsIdSet = {};
      for (let i = 0; i < this.stageList.length; i++) {
        const stage = this.stageList[i];
        const goodsId = String(stage.goodsId || "").trim();
        const label = stage.stageName || goodsId;

        // 阶段产品 ID 非空
        if (!goodsId) return "阶段产品ID不能为空";
        // 归属校验：goodsId 必须属于该套餐
        if (this.comboGoodsIds.indexOf(goodsId) === -1) {
          return `阶段产品不属于该套餐: ${label}`;
        }
        // 去重校验：不允许重复 goodsId
        if (goodsIdSet[goodsId]) {
          return `阶段产品重复: ${label}`;
        }
        goodsIdSet[goodsId] = true;
        // 数值校验：均须为 0.5 的倍数
        const requiredDays = Number(stage.requiredDays);
        const intervalDays = Number(stage.intervalDays);
        if (isNaN(requiredDays) || requiredDays < 0.5 || !this.isHalfDayMultiple(requiredDays)) {
          return `「${label}」阶段耗时天数必须>=0.5且为0.5的倍数`;
        }
        if (isNaN(intervalDays) || intervalDays < 0 || !this.isHalfDayMultiple(intervalDays)) {
          return `「${label}」阶段间隔天数必须>=0且为0.5的倍数`;
        }
      }
      return null;
    },

    /** 全量覆盖保存 */
    handleSave() {
      this.normalizeLastInterval();

      const errorMsg = this.validateStages();
      if (errorMsg) {
        this.$message({ type: "error", message: errorMsg, showClose: true });
        return;
      }

      // 全量提交：stages 即为最终结果，sort 按当前拖拽顺序重排
      const params = {
        comboId: this.comboId,
        stages: this.stageList.map((stage, index) => ({
          goodsId: String(stage.goodsId),
          stageName: stage.stageName || "",
          requiredDays: Number(stage.requiredDays),
          intervalDays: Number(stage.intervalDays),
          sort: index + 1
        }))
      };

      this.saveLoading = true;
      saveStageSchedule(params)
        .then((res) => {
          const isSuccess =
            res === true ||
            (res && (res.code === "200" || res.code === 200));
          if (isSuccess) {
            this.$message({ type: "success", message: "工期配置保存成功", showClose: true });
            this.dialogVisible = false;
            // 后端已回写 required_days，通知父组件刷新列表
            this.$emit("success");
          } else {
            this.$message({
              type: "error",
              message: (res && res.message) || "工期配置保存失败",
              showClose: true
            });
          }
        })
        .catch((err) => {
          this.$message({
            type: "error",
            message: (err && err.message) || "工期配置保存异常",
            showClose: true
          });
        })
        .finally(() => {
          this.saveLoading = false;
        });
    },

    handleClose() {
      this.stageList = [];
      this.comboGoodsIds = [];
    }
  }
};
</script>

<style lang="scss" scoped>
/* ── 套餐信息头 ──────────────────────────────────── */
.schedule-combo-info {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 12px;
  font-size: 13px;
  color: #606266;

  i {
    color: #409eff;
    font-size: 15px;
  }
}

.schedule-combo-info__name {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.schedule-combo-info__tip {
  margin-left: auto;
  font-size: 12px;
  color: #909399;
}

/* ── 加载 / 空状态 ──────────────────────────────── */
.schedule-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 48px 0;
  color: #409eff;
  font-size: 13px;

  i {
    font-size: 18px;
  }
}

.schedule-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 0;
  color: #c0c4cc;

  i {
    font-size: 36px;
    margin-bottom: 8px;
  }

  p {
    margin: 0;
    font-size: 13px;
  }
}

/* ── 工期面板 ────────────────────────────────────── */
.schedule-panel {
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  overflow: hidden;
  background: #fff;
}

.schedule-panel__header {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  background: #f5f7fa;
  border-bottom: 1px solid #ebeef5;
  font-size: 13px;
  font-weight: 600;
  color: #606266;
}

.schedule-row {
  display: flex;
  align-items: center;
  padding: 8px 14px;
  border-bottom: 1px solid #f2f6fc;
  background: #fff;
  transition: background 0.2s;

  &:hover {
    background: #f5f7fa;
  }

  &:last-child {
    border-bottom: none;
  }
}

/* 拖拽中的行样式（sortablejs ghost） */
.sortable-ghost {
  background: #ecf5ff;
  opacity: 0.7;
}

/* ── 列宽 ────────────────────────────────────────── */
.schedule-col {
  display: inline-flex;
  align-items: center;
}

.schedule-col--drag {
  width: 32px;
  flex-shrink: 0;
}

.schedule-col--sort {
  width: 48px;
  flex-shrink: 0;
}

.schedule-col--name {
  flex: 1;
  min-width: 0;
  padding-right: 10px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  color: #303133;
}

.schedule-col--days {
  width: 160px;
  flex-shrink: 0;
  gap: 6px;
}

/* ── 行内元素 ────────────────────────────────────── */
.schedule-row__drag {
  cursor: move;
  color: #909399;
  font-size: 15px;

  &:hover {
    color: #409eff;
  }
}

.schedule-row__sort-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #409eff;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
}

.schedule-row__last-tip {
  color: #e6a23c;
  font-size: 14px;
  cursor: help;
}

/* ── 底部汇总 ────────────────────────────────────── */
.schedule-panel__footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  background: #f0f9ff;
  border-top: 1px solid #e1f3f8;
  font-size: 13px;
  color: #606266;

  b {
    color: #409eff;
  }
}

.schedule-panel__total {
  font-size: 18px;
  font-weight: 700;
  color: #409eff;
}

.schedule-panel__formula {
  font-size: 12px;
  color: #909399;
}
</style>
